<template>
  <!-- Botón para abrir -->
  <button class="pgm-btn d-flex justify-content-center align-items-center" @click="open(startIndex)">
    {{ buttonLabel }} <i class="fa-solid ms-1 fa-image"></i>
  </button>

  <!-- Modal -->
  <teleport to="body">
    <div
      v-if="isOpen"
      class="pgm-overlay"
      @click.self="close"
      @keydown.esc.prevent="close"
      tabindex="0"
      ref="overlayRef"
      aria-modal="true"
      role="dialog"
      :aria-label="`Visor de imágenes (${currentIndex+1}/${images.length})`"
    >
      <div class="pgm-modal">
        <!-- Cerrar -->
        <button class="pgm-close" @click="close" aria-label="Cerrar (Esc)">×</button>

        <!-- Flechas -->
        <button
          v-if="images.length > 1"
          class="pgm-arrow left"
          @click.stop="prev"
          aria-label="Anterior"
        >‹</button>
        <button
          v-if="images.length > 1"
          class="pgm-arrow right"
          @click.stop="next"
          aria-label="Siguiente"
        >›</button>

        <!-- Área principal -->
        <div class="pgm-main">
          <img
            class="pgm-image"
            :src="images[currentIndex]"
            :alt="`Imagen ${currentIndex+1} de ${images.length}`"
            :style="imageStyle"
            @load="onLoad"
            @error="onError"
            draggable="false"
          />
          <div v-if="loading" class="pgm-loading">Cargando…</div>
        </div>

        <!-- Miniaturas -->
        <div v-if="images.length > 1" class="pgm-thumbs" role="list">
          <button
            v-for="idx in visibleThumbIndices"
            :key="`thumb-${idx}`"
            class="pgm-thumb-btn"
            :class="{ active: idx === currentIndex }"
            @click="goTo(idx)"
            :aria-label="`Ir a imagen ${idx+1}`"
            role="listitem"
          >
            <img class="pgm-thumb" :src="images[idx]" :alt="`Miniatura ${idx+1}`" draggable="false" />
          </button>
        </div>

        <!-- Contador -->
        <div class="pgm-counter">{{ currentIndex + 1 }} / {{ images.length }}</div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { nextTick } from 'vue'

const viewportLimits = {
  maxWidth: 'min(92vw, 1200px)',
  maxHeight: 'min(60vh, 900px)'
}

export default {
  name: 'PhotoGalleryModal',

  props: {
    images: { type: Array, required: true },
    startIndex: { type: Number, default: 0 },
    buttonLabel: { type: String, default: 'Galería' },
    // Ajuste de imagen: 'natural' | 'contain' | 'cover'
    fitMode: { type: String, default: 'natural' },
    // Si false, no se agranda por encima del tamaño natural
    upscale: { type: Boolean, default: false }
  },

  data() {
    return {
      isOpen: false,
      currentIndex: 0,
      loading: true,
      overlayRef: null
    }
  },

  computed: {
    imageStyle() {
      if (this.fitMode === 'natural') {
        return {
          maxWidth: viewportLimits.maxWidth,
          maxHeight: viewportLimits.maxHeight,
          width: this.upscale ? '100%' : 'auto',
          height: 'auto',
          objectFit: 'contain'
        }
      }
      if (this.fitMode === 'contain') {
        return {
          maxWidth: viewportLimits.maxWidth,
          maxHeight: viewportLimits.maxHeight,
          width: '100%',
          height: 'auto',
          objectFit: 'contain'
        }
      }
      // cover
      return {
        maxWidth: viewportLimits.maxWidth,
        maxHeight: viewportLimits.maxHeight,
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }
    },

    // Miniaturas visibles (ventana de 4 alrededor de la actual)
    visibleThumbIndices() {
      const n = this.images.length
      if (n <= 4) return Array.from({ length: n }, (_, i) => i)
      const base = this.currentIndex
      const offsets = [-1, 0, 1, 2]
      const set = offsets.map(off => this.clampIndex(base + off))
      return Array.from(new Set(set)).slice(0, 4)
    }
  },

  watch: {
    startIndex(v) {
      if (this.isOpen) this.currentIndex = this.clampIndex(v)
    }
  },

  mounted() {
    window.addEventListener('keydown', this.onKey)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey)
  },

  methods: {
    clampIndex(i) {
      const n = this.images.length || 1
      return ((i % n) + n) % n
    },

    async open(index = 0) {
      if (!this.images?.length) return
      this.currentIndex = this.clampIndex(index)
      this.loading = true
      this.isOpen = true
      await nextTick()
      this.overlayRef?.focus?.()
    },

    close() { this.isOpen = false },

    next() {
      this.loading = true
      this.currentIndex = this.clampIndex(this.currentIndex + 1)
    },

    prev() {
      this.loading = true
      this.currentIndex = this.clampIndex(this.currentIndex - 1)
    },

    goTo(idx) {
      this.loading = true
      this.currentIndex = this.clampIndex(idx)
    },

    onKey(e) {
      if (!this.isOpen) return
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next() }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.prev() }
    },

    onLoad() { this.loading = false },
    onError() { this.loading = false }
  }
}
</script>

<style scoped>
/* ===============================
   Botón
   =============================== */
.pgm-btn{
  appearance: none;
  border: none;
  padding: .7rem 1rem;
  border-radius: .40rem;
  cursor: pointer;
  background-color: #eeeeee;
  box-shadow: 0 6px 18px rgba(0,0,0,.08);
}
.pgm-btn:hover{ background-color: #d9d9d9; }

/* ===============================
   Overlay
   =============================== */
.pgm-overlay{
  position: fixed; inset: 0;
  display: flex; justify-content: center; align-items: center;
  background: rgba(0,0,0,.45);
  backdrop-filter: blur(8px) saturate(120%);
  -webkit-backdrop-filter: blur(8px) saturate(120%);
  z-index: 9999;
  outline: none;
}

/* ===============================
   Modal
   =============================== */
.pgm-modal{
  --arrow-size: 44px;
  --arrow-gap: 16px;

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: clamp(280px, 96vw, 1280px);
  max-height: 92vh;
  gap: .75rem;
}

/* Área principal */
.pgm-main{
  margin-top: 50px;
  flex: 1 1 auto;
  width: 100%;
  padding-inline: calc(var(--arrow-size) + var(--arrow-gap));
  display: flex; justify-content: center; align-items: center;
  min-height: 220px;
  position: relative;
}

/* Imagen */
.pgm-image{
  border-radius: 14px;
  height: 100% !important;
  box-shadow: 0 20px 60px rgba(0,0,0,.35);
  user-select: none;
}

/* Loader */
.pgm-loading{
  position: absolute; inset: 0;
  display: grid; place-items: center;
  color: #fff; font-weight: 600;
  text-shadow: 0 2px 6px rgba(0,0,0,.4);
}

/* Flechas */
.pgm-arrow{
  position: absolute; top: 50%;
  transform: translateY(-50%);
  width: var(--arrow-size); height: var(--arrow-size);
  border-radius: 999px; border: none;
  display: grid; place-items: center;
  background: rgba(0,0,0,.4);
  color: #fff; cursor: pointer;
  box-shadow: 0 6px 18px rgba(0,0,0,.25);
  font-size: 1.5rem; line-height: 1;
  z-index: 2;
}
.pgm-arrow.left{ left: var(--arrow-gap); }
.pgm-arrow.right{ right: var(--arrow-gap); }
.pgm-arrow:hover{ background: rgba(0,0,0,.55); }

/* Cerrar */
.pgm-close{
  position: absolute; top: 12px; right: 12px;
  width: 36px; height: 36px;
  border-radius: 999px; border: none;
  background: rgba(0,0,0,.55); color: #fff;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0,0,0,.35);
  font-size: 1.2rem;
  z-index: 3;
}

/* Miniaturas */
.pgm-thumbs{
  display: flex; gap: .5rem;
  justify-content: center; align-items: center;
  padding: .25rem .4rem .1rem;
  max-width: 96vw;
  overflow-x: auto;
  scrollbar-width: thin;
}
.pgm-thumb-btn{
  border: none; padding: 0;
  border-radius: .5rem; overflow: hidden;
  background: rgba(0,0,0,.2);
  cursor: pointer; outline: none;
  box-shadow: 0 8px 18px rgba(0,0,0,.25);
  width: 64px; height: 64px;
  display: grid; place-items: center;
  flex: 0 0 auto;
}
.pgm-thumb-btn.active{ outline: 2px solid #fff; outline-offset: 2px; }
.pgm-thumb{ width: 100%; height: 100%; object-fit: cover; display: block; }

/* Contador */
.pgm-counter{
  color: #fff; font-size: 14px;
  background: rgba(0,0,0,.4);
  padding: .25rem .6rem; border-radius: .5rem;
}

/* Accesibilidad */
.pgm-arrow:focus-visible, .pgm-close:focus-visible, .pgm-thumb-btn:focus-visible{
  outline: 2px solid #fff; outline-offset: 2px;
}

/* ====== Responsive tweaks ====== */
@media (max-width: 768px){
  .pgm-modal{
    --arrow-size: 36px;
    --arrow-gap: 10px;
  }
  .pgm-thumb-btn{ width: 56px; height: 56px; }
}

@media (max-width: 480px){
  .pgm-modal{
    --arrow-size: 30px;
    --arrow-gap: 8px;
  }
  .pgm-main{
    padding-inline: calc((var(--arrow-size) * 0.9) + var(--arrow-gap));
    min-height: 180px;
  }
  .pgm-thumb-btn{ width: 52px; height: 52px; }
}
</style>
